import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  Injector,
  input,
  OnInit,
  output,
  runInInjectionContext,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogService } from '@wheere-front/shared-util';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { Toolbar } from 'primeng/toolbar';

@Component({
  selector: 'lib-logs-session',
  standalone: true,
  imports: [CommonModule, SelectButton, FormsModule, Button, Toolbar, ScrollingModule],
  template: `
    <div class="card mb-6 flex w-full justify-between">
      <p-toolbar class="w-full">
        <ng-template #start>
          <div class="flex gap-2">
            <p-select-button
              class="filter-buttons"
              [options]="filterOptions"
              [ngModel]="value()"
              (ngModelChange)="value.set($event)"
              [multiple]="true"
              optionLabel="name"
              size="small"
              optionValue="value">
            </p-select-button>
            <p-button
              label="Download Logs"
              icon="pi pi-download"
              size="small"
              (click)="downloadLog()"
              [disabled]="rawFormattedLogs.length === 0">
            </p-button>
          </div>
        </ng-template>
        <ng-template #end>
          <p-button label="Clear Logs" icon="pi pi-trash" size="small" class="bg-red-100" (click)="clearLogs()">
          </p-button>
        </ng-template>
      </p-toolbar>
    </div>
    <!-- Terminal container with virtual scrolling -->
    <cdk-virtual-scroll-viewport
      class="terminal-container"
      [itemSize]="22"
      [minBufferPx]="440"
      [maxBufferPx]="880"
      (scrolledIndexChange)="onScrollIndexChange($event)">
      <div class="terminal-line" *cdkVirtualFor="let log of formattedLogs()" [innerHTML]="log"></div>
    </cdk-virtual-scroll-viewport>
    <div class="blinking-cursor-fixed">|</div>
  `,
  styles: [
    `
      /* Terminal Container and Log Styles */
      .terminal-container {
        background-color: #1e1e1e;
        color: #ffffff;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 14px;
        line-height: 22px;
        border-radius: 5px;
        height: 80vh;
        white-space: pre-wrap;
        contain: strict;
      }

      .terminal-line {
        height: 22px;
        padding: 0 10px;
        margin: 0;
        box-sizing: border-box;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
      }

      .cdk-virtual-scroll-content-wrapper {
        display: block;
      }

      .blinking-cursor-fixed {
        position: fixed;
        bottom: calc(20vh + 20px);
        left: 20px;
        display: inline-block;
        color: #ffffff;
        animation: blink 500ms infinite alternate;
        pointer-events: none;
        z-index: 10;
      }

      @keyframes blink {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }

      .timestamp {
        color: #999999;
      }

      .brackets {
        color: #aaaaaa;
      }

      .filter-buttons {
        flex-grow: 1;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class LogsSessionComponent implements OnInit {
  deviceId = input.required<string>();
  logs = input.required<string[]>();
  formattedLogs = signal<SafeHtml[]>([]);
  rawFormattedLogs: string[] = [];

  clearLogsOutput = output();
  sendCommand = output<string>();
  value = signal<number[]>([]);

  filterOptions: Array<{ name: string; value: number }> = [
    { name: 'Info', value: 1 },
    { name: 'Warn', value: 2 },
    { name: 'Debug', value: 3 },
    { name: 'Error', value: 4 },
  ];

  private readonly MAX_LOGS = 10000;

  @ViewChild(CdkVirtualScrollViewport, { static: true })
  viewport!: CdkVirtualScrollViewport;

  isAtBottom = true;
  private lastLogCount = 0;

  constructor(
    private sanitizer: DomSanitizer,
    private injector: Injector,
    private logService: LogService
  ) {}

  ngOnInit() {
    runInInjectionContext(this.injector, () => {
      effect(
        () => {
          const rawLogs = this.logs();
          const selected = this.value();

          const filtered =
            selected.length === 0
              ? rawLogs
              : rawLogs.filter(line => {
                  const hasInf = line.includes('INF');
                  const hasWrn = line.includes('WRN');
                  const hasDbg = line.includes('DBG');
                  const hasErr = line.includes('ERR');
                  const matchInf = selected.includes(1) && hasInf;
                  const matchWrn = selected.includes(2) && hasWrn;
                  const matchDebug = selected.includes(3) && hasDbg;
                  const matchErr = selected.includes(4) && hasErr;
                  return matchInf || matchWrn || matchDebug || matchErr;
                });

          // Limit logs to prevent memory issues
          const limitedFiltered = filtered.slice(-this.MAX_LOGS);
          this.rawFormattedLogs = limitedFiltered.map(log => this.logService.formatLogLineRaw(log));
          this.formattedLogs.set(this.rawFormattedLogs.map(line => this.sanitizer.bypassSecurityTrustHtml(line)));

          // Auto-scroll only if new logs were added and user was at bottom
          const newLogCount = this.formattedLogs().length;
          if (this.isAtBottom && newLogCount > this.lastLogCount) {
            setTimeout(() => this.scrollToBottom(), 0);
          }
          this.lastLogCount = newLogCount;
        },
        { allowSignalWrites: true }
      );
    });
  }

  onScrollIndexChange(index: number) {
    const totalItems = this.formattedLogs().length;
    const viewportSize = this.viewport.getViewportSize();
    const itemSize = 22; // matches itemSize in template
    const visibleItems = Math.ceil(viewportSize / itemSize);

    // Check if we're near the bottom (within last 3 items)
    this.isAtBottom = index + visibleItems >= totalItems - 3;
  }

  scrollToBottom() {
    const totalItems = this.formattedLogs().length;
    if (totalItems > 0) {
      this.viewport.scrollToIndex(totalItems - 1, 'smooth');
      this.isAtBottom = true;
    }
  }

  downloadLog() {
    this.logService.downloadLog(this.rawFormattedLogs, this.deviceId());
  }

  clearLogs() {
    this.formattedLogs.set([]);
    this.rawFormattedLogs = [];
    this.lastLogCount = 0;
    this.clearLogsOutput.emit();
  }
}
